// File: utils/constants.js

/**
 * Application Constants
 * 
 * Centralized location for all constants used throughout the app.
 * Import ENV_CONFIG for environment-specific values.
 */

import { ENV_CONFIG } from '../config/environments.js';

// ========================================
// Application Information
// ========================================

export const APP = {
  NAME: ENV_CONFIG.APP_NAME,
  VERSION: ENV_CONFIG.APP_VERSION,
  URL: ENV_CONFIG.APP_URL,
};

// ========================================
// Regulations
// ========================================

export const REGULATIONS = {
  '1910': {
    code: '1910',
    title: '29 CFR 1910',
    name: 'General Industry',
    description: 'Occupational Safety and Health Standards',
    icon: '📖',
    color: '#1a73e8',
    subparts: 'A-Z',
  },
  '1926': {
    code: '1926',
    title: '29 CFR 1926',
    name: 'Construction',
    description: 'Safety and Health Regulations for Construction',
    icon: '🏗️',
    color: '#f57c00',
    subparts: 'A-Z',
  },
};

export const REGULATION_CODES = Object.keys(REGULATIONS);

// ========================================
// Navigation Views
// ========================================

export const NAVIGATION_VIEWS = {
  TOPIC: 'topic',
  CODE: 'code',
};

export const VIEW_LABELS = {
  [NAVIGATION_VIEWS.TOPIC]: '🔤 By Topic',
  [NAVIGATION_VIEWS.CODE]: '🔢 By Code',
};

// ========================================
// Routes / Pages
// ========================================

export const ROUTES = {
  HOME: '/',
  REGULATION: '/regulation/:code',
  SECTION: '/regulation/:code/:section',
  SEARCH: '/search',
  BOOKMARKS: '/bookmarks',
  RECENT: '/recent',
  SETTINGS: '/settings',
};

// ========================================
// LocalStorage Keys
// ========================================

const STORAGE_PREFIX = ENV_CONFIG.STORAGE_PREFIX;

export const STORAGE_KEYS = {
  LANGUAGE: `${STORAGE_PREFIX}language`,
  NAVIGATION_VIEW: `${STORAGE_PREFIX}nav_view`,
  BOOKMARKS: `${STORAGE_PREFIX}bookmarks`,
  NOTES: `${STORAGE_PREFIX}notes`,
  RECENT_SECTIONS: `${STORAGE_PREFIX}recent`,
  THEME: `${STORAGE_PREFIX}theme`,
  SEARCH_HISTORY: `${STORAGE_PREFIX}search_history`,
  LAST_VISITED: `${STORAGE_PREFIX}last_visited`,
  DISCLAIMER_ACK: `${STORAGE_PREFIX}disclaimer_ack`,
};

// ========================================
// Feature Flags
// ========================================

export const FEATURES = {
  OFFLINE: ENV_CONFIG.ENABLE_OFFLINE,
  ANALYTICS: ENV_CONFIG.ENABLE_ANALYTICS,
  PDF_EXPORT: ENV_CONFIG.ENABLE_PDF_EXPORT,
  BOOKMARKS: ENV_CONFIG.ENABLE_BOOKMARKS,
  NOTES: ENV_CONFIG.ENABLE_NOTES,
  CONTENT_TRANSLATIONS: ENV_CONFIG.ENABLE_CONTENT_TRANSLATIONS,
};

// ========================================
// Limits
// ========================================

export const LIMITS = {
  MAX_BOOKMARKS: ENV_CONFIG.MAX_BOOKMARKS,
  MAX_NOTES: ENV_CONFIG.MAX_NOTES,
  MAX_SEARCH_RESULTS: ENV_CONFIG.MAX_SEARCH_RESULTS,
  MAX_RECENT_SECTIONS: 20,
  MAX_SEARCH_HISTORY: 10,
};

// ========================================
// Languages
// ========================================

export const LANGUAGES = {
  EN: {
    code: 'en',
    name: 'English',
    flag: '🇺🇸',
    available: true,
  },
  ES: {
    code: 'es',
    name: 'Español',
    flag: '🇪🇸',
    available: true,
  },
  FR: {
    code: 'fr',
    name: 'Français',
    flag: '🇫🇷',
    available: false, // Coming soon
  },
};

export const LANGUAGE_CODES = Object.keys(LANGUAGES).map(key => LANGUAGES[key].code);
export const DEFAULT_LANGUAGE = ENV_CONFIG.DEFAULT_LANGUAGE;

// ========================================
// UI Constants
// ========================================

export const UI = {
  SCROLL_THRESHOLD_BACK_TO_TOP: ENV_CONFIG.SCROLL_THRESHOLD_BACK_TO_TOP,
  ANIMATION_DURATION_MS: ENV_CONFIG.ANIMATION_DURATION_MS,
  TOUCH_TARGET_SIZE: ENV_CONFIG.TOUCH_TARGET_SIZE,
  SEARCH_DEBOUNCE_MS: ENV_CONFIG.SEARCH_DEBOUNCE_MS,
  
  // Breakpoints (matches CSS variables)
  BREAKPOINTS: {
    SM: 640,  // Small tablets
    MD: 768,  // Tablets
    LG: 1024, // Small desktops
    XL: 1280, // Large desktops
  },
};

// ========================================
// Data Paths
// ========================================

export const DATA_PATHS = {
  REGULATIONS: '/data/regulations.json',
  REGULATION_INDEX: (code) => `/data/${code}/index.json`,
  SECTION: (code, section) => `/data/${code}/sections/${section}.json`,
};

// ========================================
// API Configuration (if using eCFR API directly)
// ========================================

export const ECFR_API = {
  BASE_URL: 'https://www.ecfr.gov/api/versioner/v1',
  ENDPOINTS: {
    STRUCTURE: (date, title, part) => `/structure/${date}/title-${title}/part-${part}.json`,
    SECTION: (date, title, part, section) => `/full/${date}/title-${title}/part-${part}/section-${section}.json`,
  },
  DEFAULT_TITLE: '29', // Title 29 for OSHA
};

// ========================================
// Section Types
// ========================================

export const SECTION_TYPES = {
  SECTION: 'section',
  SUBPART: 'subpart',
  APPENDIX: 'appendix',
  AUTHORITY: 'authority',
};

// ========================================
// Action Types (for user actions)
// ========================================

export const ACTIONS = {
  BOOKMARK: 'bookmark',
  NOTE: 'note',
  SHARE: 'share',
  PDF: 'pdf',
  PRINT: 'print',
};

// ========================================
// Error Messages
// ========================================

export const ERROR_MESSAGES = {
  SECTION_NOT_FOUND: 'Section not found. Please check the code and try again.',
  NETWORK_ERROR: 'Unable to load data. Please check your connection.',
  STORAGE_FULL: 'Storage limit reached. Please remove some bookmarks or notes.',
  INVALID_SECTION: 'Invalid section code format.',
  GENERIC: 'Something went wrong. Please try again.',
};

// ========================================
// Success Messages
// ========================================

export const SUCCESS_MESSAGES = {
  BOOKMARK_ADDED: 'Section bookmarked successfully',
  BOOKMARK_REMOVED: 'Bookmark removed',
  NOTE_SAVED: 'Note saved successfully',
  NOTE_DELETED: 'Note deleted',
  PDF_GENERATED: 'PDF generated successfully',
  COPIED_TO_CLIPBOARD: 'Copied to clipboard',
};

// ========================================
// Regular Expressions
// ========================================

export const REGEX = {
  // Matches section codes like 1910.22 or 1926.501
  SECTION_CODE: /^(\d{4})\.(\d+)([a-z])?$/,
  
  // Matches subsection markers like (a), (b)(1), (c)(2)(i)
  SUBSECTION_MARKER: /^\(([a-z0-9]+)\)(\(([a-z0-9]+)\))?(\(([ivxlcdm]+)\))?$/i,
};

// ========================================
// Search Configuration
// ========================================

export const SEARCH = {
  MIN_QUERY_LENGTH: 2,
  DEBOUNCE_MS: ENV_CONFIG.SEARCH_DEBOUNCE_MS,
  MAX_RESULTS: ENV_CONFIG.MAX_SEARCH_RESULTS,
  HIGHLIGHT_CLASS: 'search-highlight',
};

// ========================================
// Reading Time Configuration
// ========================================

export const READING = {
  WORDS_PER_MINUTE: 200, // Average reading speed
  CALCULATE_TIME: (wordCount) => Math.ceil(wordCount / 200),
};

// ========================================
// Date Formats
// ========================================

export const DATE_FORMATS = {
  SHORT: 'MMM DD, YYYY',      // Oct 23, 2024
  LONG: 'MMMM DD, YYYY',       // October 23, 2024
  ISO: 'YYYY-MM-DD',           // 2024-10-23
  DISPLAY: 'MMM DD',           // Oct 23
};

// ========================================
// Icons (Unicode)
// ========================================

export const ICONS = {
  // Navigation
  MENU: '☰',
  BACK: '←',
  CLOSE: '✕',
  EXPAND: '▼',
  COLLAPSE: '▲',
  
  // Actions
  BOOKMARK: '⭐',
  BOOKMARK_FILLED: '⭐',
  NOTE: '📝',
  SHARE: '↗️',
  PDF: '📄',
  PRINT: '🖨️',
  SEARCH: '🔍',
  
  // Status
  SUCCESS: '✅',
  ERROR: '❌',
  WARNING: '⚠️',
  INFO: 'ℹ️',
  
  // Regulations
  GENERAL_INDUSTRY: '📖',
  CONSTRUCTION: '🏗️',
  
  // Other
  LOADING: '⏳',
  OFFLINE: '📡',
  BACK_TO_TOP: '↑',
};

// ========================================
// Validation Rules
// ========================================

export const VALIDATION = {
  NOTE_MAX_LENGTH: 1000,
  BOOKMARK_TITLE_MAX_LENGTH: 100,
  SEARCH_MAX_LENGTH: 100,
};

// ========================================
// Cache Configuration (for Service Worker)
// ========================================

export const CACHE = {
  NAME: `${STORAGE_PREFIX}cache_v1`,
  MAX_AGE_DAYS: 30,
  RESOURCES: {
    STATIC: 'static',
    SECTIONS: 'sections',
    IMAGES: 'images',
  },
};

// ========================================
// Helper Functions for Constants
// ========================================

/**
 * Get regulation by code
 */
export function getRegulation(code) {
  return REGULATIONS[code] || null;
}

/**
 * Check if section code is valid format
 */
export function isValidSectionCode(code) {
  return REGEX.SECTION_CODE.test(code);
}

/**
 * Parse section code into parts
 */
export function parseSectionCode(code) {
  const match = code.match(REGEX.SECTION_CODE);
  if (!match) return null;
  
  return {
    regulation: match[1],      // e.g., "1910"
    section: match[2],          // e.g., "22"
    subsection: match[3] || '', // e.g., "a" (if exists)
    full: code,                 // e.g., "1910.22"
  };
}

/**
 * Get full section code (for file paths)
 */
export function getSectionFileName(code) {
  const parsed = parseSectionCode(code);
  if (!parsed) return null;
  return `${parsed.regulation}.${parsed.section}.json`;
}

/**
 * Check if feature is enabled
 */
export function isFeatureEnabled(feature) {
  return FEATURES[feature] === true;
}

/**
 * Get current language
 */
export function getCurrentLanguage() {
  const stored = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
  return stored || DEFAULT_LANGUAGE;
}

// ========================================
// Export All
// ========================================

const constants = {
  APP,
  REGULATIONS,
  REGULATION_CODES,
  NAVIGATION_VIEWS,
  VIEW_LABELS,
  ROUTES,
  STORAGE_KEYS,
  FEATURES,
  LIMITS,
  LANGUAGES,
  LANGUAGE_CODES,
  DEFAULT_LANGUAGE,
  UI,
  DATA_PATHS,
  ECFR_API,
  SECTION_TYPES,
  ACTIONS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  REGEX,
  SEARCH,
  READING,
  DATE_FORMATS,
  ICONS,
  VALIDATION,
  CACHE,
  
  // Helper functions
  getRegulation,
  isValidSectionCode,
  parseSectionCode,
  getSectionFileName,
  isFeatureEnabled,
  getCurrentLanguage,
};

export default constants;
